import { t } from '@transifex/native';
import { SignUp, signUpSchema, signUpEmailStepSchema } from '~/components/auth/signup';
import { redirect } from 'react-router';
import { getSession, sessionStorage } from '~/lib/session';
import type { Route } from './+types/signup';
import { signUp } from './signup.server';
import { ssoDiscover } from '~/routes/login/login.server';
import { parseWithZod } from '@conform-to/zod';
import { handleFormErrors } from '~/lib/handleFormErrors';
import { T, useT } from '@transifex/react';
import { AuthPageLayout } from '~/components/auth/auth-page-layout';
import { PageContainer } from '~/components/ui/container';
import { Navbar, CommonNavigation } from '~/components/ui/navbar';
import { useFeatureFlag } from '~/contexts/feature-flag-context';
import { guestOnlyMiddleware } from '~/middleware/guest-only';
import { handleActionError } from '~/api/error-handler';
import { SentryServer } from '~/lib/sentry.server';

export const middleware = [guestOnlyMiddleware];

export async function action({ request, context }: Route.ActionArgs) {
  const url = new URL(request.url);
  const formData = await request.formData();
  const intent = formData.get('intent');

  // ── SSO Discover ─────────────────────────────────────────────────────────
  if (intent === 'discover') {
    const submission = parseWithZod(formData, { schema: signUpEmailStepSchema });
    if (submission.status !== 'success') {
      return submission.reply();
    }

    const redirectUri = `${url.origin}/auth/sso/callback`;
    const result = await ssoDiscover(submission.value.email, redirectUri, context, {
      tosAccepted: true,
    });

    if (result.error) {
      const formError = handleFormErrors(result, submission);
      if (formError) return formError;

      if (result.response.status === 502) {
        return submission.reply({
          formErrors: [
            t(
              "Your organization's SSO provider is temporarily unavailable. Try again in a moment."
            ),
          ],
        });
      }

      if (result.response.status === 400) {
        return submission.reply({
          formErrors: [t('Please check your email and try again.')],
        });
      }

      await handleActionError(result, context);
    }

    const discoveredMethod = result.data?.method;
    const authorizationUrl = result.data?.authorizationUrl;

    if (discoveredMethod === 'OPENID_SSO') {
      if (!authorizationUrl) {
        throw new Response('SSO misconfigured: missing authorization URL', { status: 500 });
      }
      throw redirect(authorizationUrl);
    }

    if (discoveredMethod !== 'PASSWORD') {
      throw new Response('Unexpected SSO discovery method', { status: 502 });
    }

    // PASSWORD: stash email in session, redirect to step 2
    const session = await getSession(request);
    session.set('ssoSignupEmail', submission.value.email);
    throw redirect(`/signup?${new URLSearchParams({ step: 'password' }).toString()}`, {
      headers: { 'Set-Cookie': await sessionStorage.commitSession(session) },
    });
  }

  // ── Register ──────────────────────────────────────────────────────────────
  const submission = parseWithZod(formData, { schema: signUpSchema });

  if (submission.status !== 'success') {
    return submission.reply();
  }

  const session = await getSession(request);

  if (url.searchParams.get('step') === 'password') {
    const ssoSignupEmail = session.get('ssoSignupEmail');
    if (!ssoSignupEmail) {
      throw redirect('/signup');
    }
    if (submission.value.email !== ssoSignupEmail) {
      session.unset('ssoSignupEmail');
      throw redirect('/signup', {
        headers: { 'Set-Cookie': await sessionStorage.commitSession(session) },
      });
    }
  }

  const result = await signUp(submission.value.email, submission.value.password, context);
  const formError = handleFormErrors(result, submission);
  if (formError) return formError;

  await handleActionError(result, context);

  if (!result.data?.email) {
    SentryServer.captureException('No email returned from sign up');
    throw new Response('No email', { status: 502 });
  }

  session.set('email', result.data.email);
  session.unset('ssoSignupEmail');

  return redirect('/signup/verify', {
    headers: {
      'Set-Cookie': await sessionStorage.commitSession(session),
    },
  });
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const session = await getSession(request);
  const ssoSignupEmail = session.get('ssoSignupEmail');

  if (url.searchParams.get('step') === 'password' && !ssoSignupEmail) {
    throw redirect('/signup');
  }

  return { ssoSignupEmail: ssoSignupEmail ?? null };
}

export default function SignUpPage() {
  const t = useT();
  const authUiEnabled = useFeatureFlag('auth-ui');

  if (authUiEnabled) {
    return (
      <>
        <title>{t('Sign Up - Startable')}</title>
        <AuthPageLayout>
          <h2 className="text-h1 text-text-900">
            <T _str="Start learning with Startable" />
          </h2>
          <SignUp />
        </AuthPageLayout>
      </>
    );
  }

  return (
    <>
      <title>{t('Sign Up - Startable')}</title>
      <Navbar leftSlot={<CommonNavigation />} />
      <PageContainer className="items-center justify-center">
        <div
          className="
            flex flex-col gap-lg rounded-lg bg-background-0 p-lg
            md:w-full md:max-w-[560px]
          "
        >
          <h2 className="text-h1 text-text-900">
            <T _str="Start learning with Startable" />
          </h2>
          <SignUp />
        </div>
      </PageContainer>
    </>
  );
}
